import { useId, Toaster, useToastController, ToastId } from "@fluentui/react-components";
import React, { createContext, useCallback, useContext, useRef, useState } from "react";

type ToastContextType = {
    addToast: (message: string, type: string) => void;
    removeToast: (id: string) => void;
    // toasts: Array<ToastType>;
}

type ToastControllerType = ReturnType<typeof useToastController>;

const ToastContext = createContext<ToastControllerType>(
    {
        dispatchToast: () => {
            throw new Error("dispatchToast not implemented");
        },
        dismissToast: () => {
            throw new Error("dismissToast not implemented");
        },
        dismissAllToasts: () => {
            throw new Error("dismissAllToasts not implemented");
        },
        updateToast: () => {
            throw new Error("updateToast not implemented");
        },
        pauseToast: () => {
            throw new Error("pauseToast not implemented");
        },
        playToast: () => {
            throw new Error("playToast not implemented");
        }
    }
);

export const useToast = () => {
    const context = useContext(ToastContext);

    if (!context) {
        throw new Error("useToastController must be used within a ToastProvider");
    }

    return context;
}

type ToastProviderProps = {
    children: React.ReactNode;
}

type LiveAnnouncement = {
    id: number;
    text: string;
}

const ToastProvider = ({
    children
}: ToastProviderProps) => {
    const toasterId = useId('toaster');

    const toastController = useToastController(toasterId);

    /**
     * Fluent's built-in toast announcer discards a message that matches the one it announced
     * last (see useAriaLive_unstable), so repeating an action - pressing Save again after an
     * "Upload failed" - produced no announcement at all. It also sequences messages through a
     * priority queue that can drop updates that arrive close together.
     *
     * Passing `announce` replaces that announcer outright: Toaster stops rendering its own
     * aria-live regions, so these are the only ones on the page and there is no risk of a toast
     * being announced twice. Each announcement is rendered as a newly keyed child, so an
     * identical repeated message is still a DOM insertion and is still announced.
     */
    const announcementId = useRef(0);
    const [politeAnnouncement, setPoliteAnnouncement] = useState<LiveAnnouncement | null>(null);
    const [assertiveAnnouncement, setAssertiveAnnouncement] = useState<LiveAnnouncement | null>(null);

    const announce = useCallback((message: string, options?: { politeness?: 'polite' | 'assertive' }) => {
        const text = message.trim();

        if (!text) return;

        announcementId.current += 1;
        const announcement = { id: announcementId.current, text };

        if (options?.politeness === 'assertive') {
            setAssertiveAnnouncement(announcement);
        } else {
            setPoliteAnnouncement(announcement);
        }
    }, []);

    return (
        <ToastContext.Provider value={toastController}>
            <Toaster toasterId={toasterId} announce={announce} />
            <div className="sr-only" aria-live="polite" aria-atomic="true">
                {politeAnnouncement && <span key={politeAnnouncement.id}>{politeAnnouncement.text}</span>}
            </div>
            <div className="sr-only" aria-live="assertive" aria-atomic="true">
                {assertiveAnnouncement && <span key={assertiveAnnouncement.id}>{assertiveAnnouncement.text}</span>}
            </div>
            {children}
        </ToastContext.Provider>
    )
}

export default ToastProvider